import {capitalize} from "@mui/material";
import {FormikConfig, FormikHelpers} from "formik";
import {useCreateScheduler} from "../../client/hooks";
import {useCallback, useMemo} from "react";
import {Scheduler, SchedulerDaten} from "../../api";
import {useSchedulersHandling} from "./useSchedulersHandling";
import {useSchedulerFormSchema} from "./useSchedulerFormSchema";
import {useSnackbar} from "notistack";
import {useNavigate, useSearchParams} from "react-router-dom";
import {useMutation, useQueryClient} from "react-query";
import type {SchedulerFormValues} from "../../types/schedulerTypes";

export function useSchedulerCreateForm(): FormikConfig<SchedulerFormValues> {
    const {translation} = useSchedulersHandling();
    const {enqueueSnackbar} = useSnackbar();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const presetMandantId = searchParams.get("mandantId") ?? undefined;
    const presetAufgabeId = searchParams.get("aufgabeId") ?? undefined;
    const {mutateAsync} = useSchedulerCreate();

    const schema = useSchedulerFormSchema();

    const initialTouched = useMemo(() => ({
        name: false,
        scope: false,
        mandantId: false,
    }), []);

    const initialValues: SchedulerFormValues = useMemo(() => ({
        name: "",
        scope: presetAufgabeId ? "AUFGABE" : presetMandantId ? "MANDANT" : "GLOBAL",
        mandantId: presetMandantId,
        aufgabeId: presetAufgabeId,
        timeSlots: [{weekday: "MONDAY", start: "08:00:00", end: "17:00:00", sortOrder: 1}],
        scheduleOnFreedays: false,
        scheduleOncePerTimeSlot: false,
        equidistantSchedulingEnabled: false,
        equidistantSchedulingSeconds: undefined,
        callOutsideTimeSlots: false,
        catchUpInNextWindow: true,
        reschedulingStrategies: [{attemptNumber: 1, strategy: "NEXT_SLOT"}],
        enableRandomDistribution: false,
        maxConcurrentCalls: undefined,
    }), [presetMandantId, presetAufgabeId]);

    const onSubmitCreateForm = useCallback(async (
        daten: SchedulerFormValues,
        {setSubmitting, setStatus}: FormikHelpers<SchedulerFormValues>
    ): Promise<void> => {
        try {
            const scheduler: Scheduler = await mutateAsync({
                name: daten.name,
                scope: daten.scope,
                mandantId: daten.scope !== 'GLOBAL' ? daten.mandantId : undefined,
                aufgabeId: daten.scope === 'AUFGABE' ? daten.aufgabeId : undefined,
                timeSlots: daten.timeSlots,
                scheduleOnFreedays: daten.scheduleOnFreedays,
                scheduleOncePerTimeSlot: daten.scheduleOncePerTimeSlot,
                equidistantSchedulingEnabled: daten.equidistantSchedulingEnabled,
                equidistantSchedulingSeconds: daten.equidistantSchedulingSeconds,
                callOutsideTimeSlots: daten.callOutsideTimeSlots,
                catchUpInNextWindow: daten.catchUpInNextWindow,
                reschedulingStrategies: daten.reschedulingStrategies,
                enableRandomDistribution: daten.enableRandomDistribution,
                maxConcurrentCalls: daten.maxConcurrentCalls,
            });
            setStatus(undefined);
            enqueueSnackbar(capitalize(translation('scheduler-creation-successful')), {
                variant: "success",
            });
            navigate(`/schedulers/${scheduler.id}`);
        } catch (error: unknown) {
            enqueueSnackbar(capitalize(translation('scheduler-creation-failed')), {
                variant: "error",
            });
            if (error instanceof Error) {
                setStatus(error.message);
            } else {
                setStatus("An unknown error occurred.");
            }
        } finally {
            setSubmitting(false);
        }
    }, [mutateAsync, enqueueSnackbar, translation, navigate]);

    return useMemo(
        () => ({
            validationSchema: schema,
            validateOnMount: true,
            enableReinitialize: true,
            initialTouched: initialTouched,
            initialValues: initialValues,
            onSubmit: onSubmitCreateForm
        }),
        [schema, initialValues, initialTouched, onSubmitCreateForm]
    );
}

function useSchedulerCreate() {
    const createScheduler = useCreateScheduler();
    const queryClient = useQueryClient();

    return useMutation(
        (input: SchedulerDaten): Promise<Scheduler> => {
            return createScheduler(input);
        },
        {
            onSuccess: async () => {
                await queryClient.invalidateQueries(["schedulers"]);
            },
        }
    );
}
